/**
 * AppearanceSettings Component - Tile appearance defaults configuration
 */
import React from 'react';
import { 
  FileText, 
  FormInput, 
  ExternalLink, 
  Link2, 
  Square, 
  Play, 
  CheckSquare,
  ArrowRight,
  RotateCcw,
  Database
} from 'lucide-react';
import StandardizedTileLayout from './Grid/StandardizedTileLayout';
import TileDefaultsPanel from './TileDefaultsPanel';
import ColorPicker from './Grid/ColorPicker';
import { TileAdaptation } from '../lib/gridUtils';
import { useAppearanceSettings } from '../contexts/AppearanceSettingsContext';

// Tile type definitions for the gallery
const tileTypes = [
  {
    id: 'content',
    name: 'Content Tile',
    icon: FileText,
    defaultConfig: {
      backgroundColor: '#ffffff',
      gradientColor: '',
      gradientDirection: 'to-br',
      textColor: '#111827',
      fontSize: '14px',
      fontWeight: 'normal',
      fontStyle: 'normal',
      textDecoration: 'none',
      textAlign: 'left',
      descriptionTextColor: '#6b7280',
      descriptionFontSize: '12px',
      descriptionFontWeight: 'normal',
      descriptionFontStyle: 'normal',
      descriptionTextDecoration: 'none',
      descriptionTextAlign: 'left',
      headerAlignment: 'left',
      borderColor: '#e5e7eb',
      borderThickness: '1',
      dividerColor: '#e5e7eb',
      dividerThickness: '1px',
      shape: 'rectangle',
      backgroundImage: null,
      backgroundImageFit: 'fill',
      restrictDelete: false
    }
  },
  {
    id: 'form',
    name: 'Form Tile',
    icon: FormInput,
    defaultConfig: {
      backgroundColor: '#f0f9ff',
      gradientColor: '#f0f9ff',
      gradientDirection: 'to-br',
      textColor: '#1e40af',
      fontSize: '14px',
      fontWeight: 'normal',
      fontStyle: 'normal',
      textDecoration: 'none',
      textAlign: 'left',
      descriptionTextColor: '#6b7280',
      descriptionFontSize: '12px',
      descriptionFontWeight: 'normal',
      descriptionFontStyle: 'normal',
      descriptionTextDecoration: 'none',
      descriptionTextAlign: 'left',
      headerAlignment: 'left',
      borderColor: '#e5e7eb',
      borderThickness: '1',
      dividerColor: '#e5e7eb',
      dividerThickness: '1px',
      shape: 'rectangle',
      backgroundImage: null,
      backgroundImageFit: 'fill',
      restrictDelete: false
    }
  },
  {
    id: 'external_link',
    name: 'External Link Tile',
    icon: ExternalLink,
    defaultConfig: {
      backgroundColor: '#fef3c7',
      gradientColor: '#fef3c7',
      gradientDirection: 'to-br',
      textColor: '#92400e',
      fontSize: '14px',
      fontWeight: 'normal',
      fontStyle: 'normal',
      textDecoration: 'none',
      textAlign: 'left',
      descriptionTextColor: '#6b7280',
      descriptionFontSize: '12px',
      descriptionFontWeight: 'normal',
      descriptionFontStyle: 'normal',
      descriptionTextDecoration: 'none',
      descriptionTextAlign: 'left',
      headerAlignment: 'left',
      borderColor: '#e5e7eb',
      borderThickness: '1',
      dividerColor: '#e5e7eb',
      dividerThickness: '1px',
      shape: 'rectangle',
      backgroundImage: null,
      backgroundImageFit: 'fill',
      warningTextColor: '#d97706',
      warningBackgroundColor: '#fef3c7',
      restrictDelete: false
    }
  },
  {
    id: 'shortcut',
    name: 'Shortcut Tile',
    icon: Link2,
    defaultConfig: {
      backgroundColor: '#ecfdf5',
      gradientColor: '#ecfdf5',
      gradientDirection: 'to-br',
      textColor: '#065f46',
      fontSize: '14px',
      fontWeight: 'normal',
      fontStyle: 'normal',
      textDecoration: 'none',
      textAlign: 'left',
      descriptionTextColor: '#6b7280',
      descriptionFontSize: '12px',
      descriptionFontWeight: 'normal',
      descriptionFontStyle: 'normal',
      descriptionTextDecoration: 'none',
      descriptionTextAlign: 'left',
      headerAlignment: 'left',
      borderColor: '#e5e7eb',
      borderThickness: '1',
      dividerColor: '#e5e7eb',
      dividerThickness: '1px',
      shape: 'rectangle',
      backgroundImage: null,
      backgroundImageFit: 'fill',
      warningTextColor: '#d97706',
      warningBackgroundColor: '#fef3c7',
      restrictDelete: false
    }
  },
  {
    id: 'nested_page',
    name: 'Nested Page Tile',
    icon: ArrowRight,
    defaultConfig: {
      backgroundColor: '#fef2f2',
      gradientColor: '#fef2f2',
      gradientDirection: 'to-br',
      textColor: '#991b1b',
      fontSize: '14px',
      fontWeight: 'normal',
      fontStyle: 'normal',
      textDecoration: 'none',
      textAlign: 'left',
      descriptionTextColor: '#6b7280',
      descriptionFontSize: '12px',
      descriptionFontWeight: 'normal',
      descriptionFontStyle: 'normal',
      descriptionTextDecoration: 'none',
      descriptionTextAlign: 'left',
      headerAlignment: 'left',
      borderColor: '#e5e7eb',
      borderThickness: '1',
      dividerColor: '#e5e7eb',
      dividerThickness: '1px',
      shape: 'rectangle',
      backgroundImage: null,
      backgroundImageFit: 'fill',
      restrictDelete: false
    }
  },
  {
    id: 'roadmap',
    name: 'Roadmap Tile',
    icon: Square,
    defaultConfig: {
      backgroundColor: '#ffffff',
      gradientColor: '',
      gradientDirection: 'to-br',
      textColor: '#111827',
      fontSize: '14px',
      fontWeight: 'normal',
      fontStyle: 'normal',
      textDecoration: 'none',
      textAlign: 'left',
      descriptionTextColor: '#6b7280',
      descriptionFontSize: '12px',
      descriptionFontWeight: 'normal',
      descriptionFontStyle: 'normal',
      descriptionTextDecoration: 'none',
      descriptionTextAlign: 'left',
      headerAlignment: 'left',
      borderColor: '#e5e7eb',
      borderThickness: '1',
      dividerColor: '#e5e7eb',
      dividerThickness: '1px',
      shape: 'rectangle',
      backgroundImage: null,
      backgroundImageFit: 'fill',
      inProgressColor: '#dcfce7',
      completeColor: '#f3f4f6',
      restrictDelete: false
    }
  }
];

interface AppearanceSettingsProps {
  modalRef: React.RefObject<HTMLDivElement>;
}

export default function AppearanceSettings({ modalRef }: AppearanceSettingsProps) {
  const {
    selectedTile,
    selectedHeader,
    selectedRoadmapState,
    tileConfigs,
    headerConfigs,
    isDarkMode,
    defaultGridBackgroundColor,
    setSelectedTile,
    setSelectedHeader,
    setSelectedRoadmapState,
    toggleDarkMode,
    updateDefaultGridBackgroundColor
  } = useAppearanceSettings();
  


  // Organization ID is now set automatically in AppearanceSettingsContext when it initializes

  // Mock adaptation for consistent tile sizing - matches real tile adaptation
  const mockAdaptation: TileAdaptation = {
    level: 'medium' as const,
    padding: 16, // Proper padding for medium tiles
    showDescription: true,
    descriptionOpacity: 1.0, // Full opacity for clear preview
    maxTitleLines: 2,
    maxDescriptionLines: 2
  };

  const handleTileSelect = (tileId: string) => {
    setSelectedTile(tileId);
  };

  const handleHeaderSelect = (headerType: 'row' | 'column') => {
    setSelectedHeader(headerType);
  };

  const toggleRoadmapState = () => {
    setSelectedRoadmapState(current => {
      console.log('🔄 Toggle roadmap state from:', current);
      const newState = (() => {
        switch (current) {
          case 'todo': return 'in_progress';
          case 'in_progress': return 'complete';
          case 'complete': return 'todo';
          default: return 'todo';
        }
      })();
      console.log('🔄 Toggle roadmap state to:', newState);
      return newState;
    });
  };

  const getRoadmapIcon = () => {
    switch (selectedRoadmapState) {
      case 'in_progress': return Play;
      case 'complete': return CheckSquare;
      default: return Square;
    }
  };

  // Get complete roadmap config based on current state (merges base + state-specific)
  const getRoadmapConfig = () => {
    const roadmapConfig = tileConfigs.roadmap || {};
    // Convert state names to match context keys
    const stateKeyMap = {
      'todo': 'todoState',
      'in_progress': 'inProgressState', 
      'complete': 'completeState'
    };
    const stateKey = stateKeyMap[selectedRoadmapState];
    const stateConfig = roadmapConfig[stateKey] || {};
    
    // Merge base config with state-specific config (state config takes precedence)
    return { ...roadmapConfig, ...stateConfig };
  };

  const renderTilePreview = (tileType: any) => {
    // For roadmap tiles, use the merged state-specific config
    const config = tileType.id === 'roadmap' ? getRoadmapConfig() : tileConfigs[tileType.id];
    const RoadmapIcon = getRoadmapIcon();
    
    // Create mock tile data with all config properties
    const mockTile = {
      id: `preview-${tileType.id}`,
      type: tileType.id,
      title: tileType.name,
      description: 'Description',
      position: { row: 1, column: 1 },
      size: { width: 1, height: 1 },
      config: {
        // Use the merged config (includes state-specific overrides for roadmap)
        ...config,
        roadmapState: tileType.id === 'roadmap' ? selectedRoadmapState : undefined
      }
    };

    // Create shape style function - matches GridTile.tsx logic
    const getShapeStyle = () => {
      const shape = config.shape || 'rectangle';
      const backgroundColor = config.backgroundColor || '#ffffff';
      const borderColor = config.borderColor || '#e5e7eb';
      const borderWidth = config.borderThickness ? `${config.borderThickness}px` : '2px';
      
      // Handle gradient background
      let backgroundImage = 'none';
      if (config.gradientColor && config.gradientColor.trim() !== '') {
        const directionMap: Record<string, string> = {
          'to-r': 'to right', 'to-l': 'to left', 'to-b': 'to bottom', 'to-t': 'to top',
          'to-br': 'to bottom right', 'to-bl': 'to bottom left', 'to-tr': 'to top right', 'to-tl': 'to top left'
        };
        const cssDirection = directionMap[config.gradientDirection || 'to-br'] || 'to bottom right';
        const startColor = backgroundColor;
        const endColor = config.gradientColor;
        backgroundImage = `linear-gradient(${cssDirection}, ${startColor}, ${endColor})`;
      }

      switch (shape) {
        case 'rounded-rectangle':
          return { 
            backgroundColor,
            backgroundImage,
            borderColor,
            borderWidth,
            borderStyle: 'solid',
            borderRadius: '16px',
            clipPath: 'none'
          };
        case 'oval':
          return { 
            backgroundColor,
            backgroundImage,
            borderColor,
            borderWidth,
            borderStyle: 'solid',
            borderRadius: '50%',
            clipPath: 'none'
          };
        case 'diamond':
          return {
            backgroundColor,
            backgroundImage,
            borderColor,
            borderWidth,
            borderStyle: 'solid',
            borderRadius: '4px',
            clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)'
          };
        case 'chevron':
          return {
            backgroundColor,
            backgroundImage,
            borderColor,
            borderWidth,
            borderStyle: 'solid',
            borderRadius: '4px',
            clipPath: 'polygon(0% 0%, 85% 0%, 100% 50%, 85% 100%, 0% 100%, 15% 50%)'
          };
        case 'parallelogram':
          return {
            backgroundColor,
            backgroundImage,
            borderColor,
            borderWidth,
            borderStyle: 'solid',
            borderRadius: '4px',
            clipPath: 'polygon(15% 0%, 100% 0%, 85% 100%, 0% 100%)'
          };
        case 'hexagon':
          return {
            backgroundColor,
            backgroundImage,
            borderColor,
            borderWidth,
            borderStyle: 'solid',
            borderRadius: '4px',
            clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)'
          };
        case 'rectangle':
        default:
          return {
            backgroundColor,
            backgroundImage,
            borderColor,
            borderWidth,
            borderStyle: 'solid',
            borderRadius: '0px',
            clipPath: 'none'
          };
      }
    };

    const shapeStyle = getShapeStyle();

    return (
      <div
        key={tileType.id}
        className={`p-1.5 rounded-lg border-2 transition-all cursor-pointer ${
          selectedTile === tileType.id
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-200 hover:border-gray-300 bg-white'
        }`}
        onClick={() => handleTileSelect(tileType.id)}
      >
        {/* Gallery section with padding - selection border is around this */}
        <div className="h-32 w-full">
          {/* Tile inside the gallery section with margin to show padding */}
          <div className="h-full w-full relative rounded border border-gray-100 shadow-sm overflow-hidden">
            {/* Background layer (gradient/solid color) - matches GridTile.tsx structure */}
            <div
              className="absolute inset-0"
              style={{
                // Apply shape clipping but NO border properties
                borderRadius: shapeStyle.borderRadius,
                clipPath: shapeStyle.clipPath,
                zIndex: 10,
                // Apply gradient or solid background
                ...(config.gradientColor && config.gradientColor.trim() !== '' ? {
                  backgroundImage: shapeStyle.backgroundImage,
                  backgroundColor: 'transparent'
                } : {
                  backgroundColor: shapeStyle.backgroundColor
                })
              }}
            />
            
            {/* Image layer (on top of background) - matches GridTile.tsx structure */}
            {config.backgroundImage && (
              <div
                className="absolute inset-0"
                style={{
                  // Apply same shape clipping as background
                  borderRadius: shapeStyle.borderRadius,
                  clipPath: shapeStyle.clipPath,
                  zIndex: 15,
                  // Apply image properties
                  backgroundImage: `url(${config.backgroundImage})`,
                  backgroundSize: config.backgroundImageFit === 'stretch' ? '100% 100%' 
                    : config.backgroundImageFit === 'fill' ? 'cover' 
                    : 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundColor: 'transparent'
                }}
              />
            )}
            
            {/* Border layer (on top of image) - separate border elements that don't get clipped */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                zIndex: 20
              }}
            >
              {shapeStyle.clipPath !== 'none' ? (
                /* For clipped shapes: Use SVG to draw border outline */
                <svg
                  className="absolute inset-0 w-full h-full"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  style={{
                    overflow: 'visible'
                  }}
                >
                  <path
                    d={(() => {
                      const shape = config.shape || 'rectangle';
                      
                      // Convert clipPath polygon coordinates to SVG path (scaled to viewBox)
                      switch (shape) {
                        case 'diamond':
                          return 'M 50 0 L 100 50 L 50 100 L 0 50 Z';
                        case 'hexagon':
                          return 'M 25 0 L 75 0 L 100 50 L 75 100 L 25 100 L 0 50 Z';
                        case 'chevron':
                          return 'M 0 0 L 85 0 L 100 50 L 85 100 L 0 100 L 15 50 Z';
                        case 'parallelogram':
                          return 'M 15 0 L 100 0 L 85 100 L 0 100 Z';
                        default:
                          return 'M 0 0 L 100 0 L 100 100 L 0 100 Z';
                      }
                    })()}
                    fill="none"
                    stroke={shapeStyle.borderColor}
                    strokeWidth={shapeStyle.borderWidth}
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
              ) : (
                /* For non-clipped shapes: Use regular border */
                <div
                  className="absolute inset-0"
                  style={{
                    borderColor: shapeStyle.borderColor,
                    borderWidth: shapeStyle.borderWidth,
                    borderStyle: shapeStyle.borderStyle,
                    borderRadius: shapeStyle.borderRadius,
                    backgroundColor: 'transparent'
                  }}
                />
              )}
            </div>
            
            {/* Content layer */}
            <div className="relative z-30 h-full">
              <StandardizedTileLayout
                tile={mockTile}
                adaptation={mockAdaptation}
                mode="read"
                isSelected={false}
                tileIcon={tileType.id === 'roadmap' ? RoadmapIcon : tileType.icon}
                titleContent={tileType.name}
                descriptionContent="Description"
                specialIndicator={
                  (tileType.id === 'external_link' || tileType.id === 'shortcut') ? {
                    content: (
                      <span 
                        className="text-xs px-2 py-1 rounded-full"
                        style={{
                          backgroundColor: config.warningBackgroundColor || '#fef3c7',
                          color: config.warningTextColor || '#d97706'
                        }}
                      >
                        Not configured
                      </span>
                    ),
                    tooltipText: 'This tile needs to be configured'
                  } : undefined
                }
                actionButtons={
                  tileType.id === 'roadmap' ? (
                    <button 
                      className="p-1 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors opacity-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleRoadmapState();
                      }}
                      title={`Toggle status (current: ${selectedRoadmapState?.replace?.('_', ' ') || selectedRoadmapState || 'unknown'})`}
                    >
                      <RotateCcw className="h-3 w-3" />
                    </button>
                  ) : tileType.id === 'form' ? (
                    <button className="p-1 bg-green-500 text-white rounded opacity-50 cursor-not-allowed" title="Go to register (preview only)">
                      <Database className="h-3 w-3" />
                    </button>
                  ) : undefined
                }
                onTileClick={() => {}}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRowHeaderPreview = () => {
    const isSelected = selectedHeader === 'row';
    const config = headerConfigs['row'];
    
    // Create background style with gradient support
    const getBackgroundStyle = (opacity = 1) => {
      const baseColor = config.backgroundColor || '#f3f4f6';
      const gradientColor = config.gradientColor;
      
      if (gradientColor && gradientColor.trim() !== '') {
        const directionMap: Record<string, string> = {
          'to-r': 'to right', 'to-l': 'to left', 'to-b': 'to bottom', 'to-t': 'to top',
          'to-br': 'to bottom right', 'to-bl': 'to bottom left', 'to-tr': 'to top right', 'to-tl': 'to top left'
        };
        const cssDirection = directionMap[config.gradientDirection || 'to-br'] || 'to bottom right';
        return { background: `linear-gradient(${cssDirection}, ${baseColor}, ${gradientColor})`, opacity };
      }
      return { backgroundColor: baseColor, opacity };
    };
    
    return (
      <div
        className={`col-span-3 p-3 rounded-lg border-2 transition-all cursor-pointer ${
          isSelected
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-200 hover:border-gray-300 bg-white'
        }`}
        onClick={() => handleHeaderSelect('row')}
      >
        <div className="h-32 w-full rounded overflow-hidden flex">
          {/* Header column (full styling) */}
          <div 
            className={`w-1/3 h-full flex text-sm ${
              config.textAlign === 'left' ? 'justify-start' :
              config.textAlign === 'right' ? 'justify-end' :
              'justify-center'
            } ${
              config.alignItems === 'top' ? 'items-start' :
              config.alignItems === 'bottom' ? 'items-end' :
              'items-center'
            }`}
            style={{ 
              ...getBackgroundStyle(1),
              color: config.textColor || '#374151',
              fontWeight: config.fontWeight || 'medium',
              fontStyle: config.fontStyle || 'normal',
              textDecoration: config.textDecoration || 'none',
              fontSize: config.fontSize || '14px',
              border: config.outlineThickness && config.outlineThickness !== '0px' 
                ? `${config.outlineThickness} solid ${config.outlineColor || '#e5e7eb'}` 
                : 'none'
            }}
          >
            <span style={{ textAlign: config.textAlign || 'center' }}>Row Header</span>
          </div>
          {/* Background columns showing row background color */}
          <div 
            className="w-2/3 h-full"
            style={{
              ...getBackgroundStyle(0.3), // 30% opacity to show it's background
              border: config.applyBorderToBackground && config.outlineThickness && config.outlineThickness !== '0px' 
                ? `${config.outlineThickness} solid ${config.outlineColor || '#e5e7eb'}` 
                : 'none'
            }}
          />
        </div>
      </div>
    );
  };

  const renderColumnHeaderPreview = () => {
    const isSelected = selectedHeader === 'column';
    const config = headerConfigs['column'];
    
    // Create background style with gradient support
    const getBackgroundStyle = (opacity = 1) => {
      const baseColor = config.backgroundColor || '#f3f4f6';
      const gradientColor = config.gradientColor;
      
      if (gradientColor && gradientColor.trim() !== '') {
        const directionMap: Record<string, string> = {
          'to-r': 'to right', 'to-l': 'to left', 'to-b': 'to bottom', 'to-t': 'to top',
          'to-br': 'to bottom right', 'to-bl': 'to bottom left', 'to-tr': 'to top right', 'to-tl': 'to top left'
        };
        const cssDirection = directionMap[config.gradientDirection || 'to-br'] || 'to bottom right';
        return { background: `linear-gradient(${cssDirection}, ${baseColor}, ${gradientColor})`, opacity };
      }
      return { backgroundColor: baseColor, opacity };
    };
    
    return (
      <div
        className={`row-span-6 p-3 rounded-lg border-2 transition-all cursor-pointer ${
          isSelected
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-200 hover:border-gray-300 bg-white'
        }`}
        onClick={() => handleHeaderSelect('column')}
      >
        <div className="h-full w-full rounded overflow-hidden flex flex-col">
          {/* Header row (full styling) - matches actual 40px column header height */}
          <div 
            className={`h-10 w-full flex items-center text-sm ${
              config.textAlign === 'left' ? 'justify-start' :
              config.textAlign === 'right' ? 'justify-end' :
              'justify-center'
            }`}
            style={{ 
              ...getBackgroundStyle(1),
              color: config.textColor || '#374151',
              fontWeight: config.fontWeight || 'medium',
              fontStyle: config.fontStyle || 'normal',
              textDecoration: config.textDecoration || 'none',
              fontSize: config.fontSize || '14px',
              border: config.outlineThickness && config.outlineThickness !== '0px' 
                ? `${config.outlineThickness} solid ${config.outlineColor || '#e5e7eb'}` 
                : 'none'
            }}
          >
            <span style={{ textAlign: config.textAlign || 'center' }}>Column Header</span>
          </div>
          {/* Background rows showing column background color */}
          <div 
            className="flex-1 w-full"
            style={{
              ...getBackgroundStyle(0.3), // 30% opacity to show it's background
              border: config.applyBorderToBackground && config.outlineThickness && config.outlineThickness !== '0px' 
                ? `${config.outlineThickness} solid ${config.outlineColor || '#e5e7eb'}` 
                : 'none'
            }}
          />
        </div>
      </div>
    );
  };

  const closeTileDefaultsPanel = () => {
    setSelectedTile(null);
    setSelectedHeader(null);
  };

  const hasSelection = selectedTile || selectedHeader;

  const handleHeaderClick = (e: React.MouseEvent) => {
    // Only deselect if clicking directly on the header area
    if (e.target === e.currentTarget) {
      setSelectedTile(null);
      setSelectedHeader(null);
    }
  };

  return (
    <>
      {/* Main Gallery Area - Always same size */}
      <div className="w-full p-6 overflow-y-auto" onClick={handleHeaderClick}>
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-2">Appearance Defaults</h3>
          <p className="text-sm text-muted-foreground">
            Configure the default visual style for tile types and headers. Select any element to customize its appearance.
          </p>
        </div>

        {/* Global Settings */}
        <div className="mb-4 p-3 bg-card border border-border rounded-lg">
          <h4 className="text-sm font-medium text-card-foreground mb-2">Global Settings</h4>
          <div className="space-y-3">
            {/* Dark Mode Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-foreground">Dark Mode</label>
                <p className="text-xs text-muted-foreground">Switch between light and dark theme for the entire site</p>
              </div>
              <button
                onClick={toggleDarkMode}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isDarkMode ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isDarkMode ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Grid Background Color Default */}
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-foreground">Default Grid Background Color</label>
                <p className="text-xs text-muted-foreground">Default background color applied to new pages</p>
              </div>
              <div className="flex items-center space-x-2">
                <ColorPicker
                  label=""
                  value={defaultGridBackgroundColor}
                  onChange={updateDefaultGridBackgroundColor}
                  showOpacity={true}
                  allowClear={true}
                />
              </div>
            </div>
          </div>
        </div>

        <div 
          className="grid grid-cols-4 grid-rows-6 gap-1 p-4 rounded-lg border border-border" 
          onClick={(e) => e.stopPropagation()}
          style={{ backgroundColor: defaultGridBackgroundColor }}
        >
          {/* Row Header - spans columns 1-3 */}
          {renderRowHeaderPreview()}
          
          {/* Column Header - spans rows 1-3 in column 4 */}
          {renderColumnHeaderPreview()}
          
          {/* Row 2: Tile Types */}
          {tileTypes.slice(0, 3).map(renderTilePreview)}
          
          {/* Row 3: More Tile Types */}
          {tileTypes.slice(3, 6).map(renderTilePreview)}
          
          {/* Row 4: Remaining tiles and empty spaces */}
          {tileTypes.slice(6).map(renderTilePreview)}
          {Array.from({ length: 4 - (tileTypes.length - 6) }, (_, i) => (
            <div key={`empty-row4-${i}`} className="opacity-50">
              <div className="p-2 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50">
                <div className="h-16 w-full flex items-center justify-center text-gray-400 text-xs">
                  Future Tile Type
                </div>
              </div>
            </div>
          ))}

          {/* More empty spaces for future expansion - Row 5 */}
          {Array.from({ length: 4 }, (_, i) => (
            <div key={`empty-row5-${i}`} className="opacity-50">
              <div className="p-2 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50">
                <div className="h-16 w-full flex items-center justify-center text-gray-400 text-xs">
                  Future Tile Type
                </div>
              </div>
            </div>
          ))}

          {/* Row 6 - Additional empty spaces */}
          {Array.from({ length: 4 }, (_, i) => (
            <div key={`empty-row6-${i}`} className="opacity-50">
              <div className="p-2 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50">
                <div className="h-16 w-full flex items-center justify-center text-gray-400 text-xs">
                  Future Tile Type
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Separate Tile Defaults Panel */}
      <TileDefaultsPanel
        isOpen={!!hasSelection}
        onClose={closeTileDefaultsPanel}
        modalRef={modalRef}
      />
    </>
  );
}
